import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Stack,
  Typography,
  Paper,
  Box,
  Collapse,
  Link,
  Theme,
  Button,
  ButtonBase,
  Tooltip,
} from '@mui/material';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeSanitize from 'rehype-sanitize';
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import CodeEmbed from './CodeEmbed';
import type {
  CodeSnippetCodeByDropZone,
  CodeSnippetDropZone,
  CodeSnippetDropZoneMode,
  CodeSnippetImport,
  CodeSnippetInsertionOutcome,
  CodeSnippetInsertionPayload,
  CodeSnippetPlacementFilter,
  CodeSnippetSource,
  CodeSnippetSourceFilter,
  CodeSnippetSupportingReceiver,
} from '../types';
import { aggregateExecutionModelForDropZone, codeTypeMatchesDropZone, EXECUTION_MODEL_DROP_ZONES } from '../utils/snippetExecutionModel';
import { API_SNIPPET_NAVIGATION_ITEM_SELECTOR } from '../Snippets/snippetKeyboardNavigation';

type TranslationFunction = ReturnType<typeof useTranslation>['t'];

const ALL_EXECUTION_MODELS_EXPANDED: Record<CodeSnippetDropZoneMode, boolean> = {
  'top-level-setup': true,
  contextual: true,
  'action-group': true,
};

interface CodeSnippetI18nText {
  key: string;
  defaultValue: string;
  values?: Record<string, string | number>;
}

interface CodeSnippetCategorySection {
  key: string;
  title: string;
  titleI18n?: CodeSnippetI18nText;
}

export interface CodeSnippet {
  key: string;
  metadataPath?: string;
  title: string;
  titleI18n?: CodeSnippetI18nText;
  icon?: JSX.Element;
  iconComponent?: React.ElementType;
  code?: string;
  insertText?: string;
  display?: string;
  codeByDropZone?: CodeSnippetCodeByDropZone;
  displayByDropZone?: CodeSnippetCodeByDropZone;
  codeLines?: string[];
  displayLines?: string[];
  receiverClassName?: string;
  supportingReceiver?: CodeSnippetSupportingReceiver;
  type?: string;
  dropZone?: CodeSnippetDropZoneMode;
  source?: CodeSnippetSource;
  imports?: CodeSnippetImport[];
  frameworkClassName?: string;
  children?: CodeSnippet[];
  description?: string;
  descriptionI18n?: CodeSnippetI18nText;
  more_info?: string;
  presentation?: 'section';
  categorySection?: CodeSnippetCategorySection;
  isLiveRobotFeedback?: boolean;
  requiresCommandManagement?: boolean;
  commandManagementAvailability?: 'enabled' | 'disabled';
  disabledReasonI18n?: CodeSnippetI18nText;
}

export interface CodeSnippetCategoryHeader {
  title: string;
  description?: string;
  sectionTitle?: string;
}

interface CodeContentProps {
  snippet: CodeSnippet;
  extraContentTop?: React.ReactNode;
  filterText?: string;
  placementFilter?: CodeSnippetPlacementFilter;
  sourceFilter?: CodeSnippetSourceFilter;
  emptyFilterMessage?: string;
  commandManagementEnabled: boolean;
  hideDisabledSnippets?: boolean;
  onInsertSnippet?: (payload: CodeSnippetInsertionPayload) => CodeSnippetInsertionOutcome;
  groupSnippetsByExecutionModel?: boolean;
  keyboardNavigation?: {
    containerId: string;
    instructionsId: string;
    onVisibleSnippetCountChange?: (count: number) => void;
  };
  categoryNavigation?: {
    selectedCategoryKey: string | null;
    onSelectCategory: (key: string) => void;
    onBack: () => void;
    hideBackControl?: boolean;
    hideSelectedCategoryHeader?: boolean;
    onSelectedCategoryHeaderChange?: (header: CodeSnippetCategoryHeader | null) => void;
  };
}

interface ExecutionModelSection {
  dropZone: CodeSnippetDropZoneMode;
  title: string;
  description: string;
}

// Normalize markdown from i18n/Locize - ensures newlines are proper for fenced code blocks
const normalizeMarkdown = (s: string): string => (s ?? '').replace(/\\n/g, '\n').replace(/\r\n/g, '\n').replace(/\r/g, '\n');

const interpolateDefault = (defaultValue: string, values: Record<string, string | number> = {}): string =>
  defaultValue.replace(/\{\{(\w+)\}\}/g, (_match, name: string) => String(values[name] ?? ''));

const getSnippetDescription = (snippet: CodeSnippet, t: TranslationFunction): string | undefined => {
  if (!snippet.descriptionI18n) return snippet.description;

  const { key, defaultValue, values } = snippet.descriptionI18n;
  const translated = t(key, { defaultValue, ...values });
  return typeof translated === 'string' ? translated : interpolateDefault(defaultValue, values);
};

const getSnippetTitle = (snippet: CodeSnippet, t: TranslationFunction): string => {
  if (!snippet.titleI18n) return snippet.title;

  const { key, defaultValue, values } = snippet.titleI18n;
  const translated = t(key, { defaultValue, ...values });
  return typeof translated === 'string' ? translated : interpolateDefault(defaultValue, values);
};

const getCategorySectionTitle = (section: CodeSnippetCategorySection, t: TranslationFunction): string => {
  if (!section.titleI18n) return section.title;

  const { key, defaultValue, values } = section.titleI18n;
  const translated = t(key, { defaultValue, ...values });
  return typeof translated === 'string' ? translated : interpolateDefault(defaultValue, values);
};

const getSnippetDisabledReason = (snippet: CodeSnippet, t: TranslationFunction): string => {
  const fallback = t('snippets.disabled.requiresRobotBase', 'Turn on Use RobotBase to use this snippet.');
  if (!snippet.disabledReasonI18n) return fallback;

  const { key, defaultValue, values } = snippet.disabledReasonI18n;
  const translated = t(key, { defaultValue, ...values });
  return typeof translated === 'string' ? translated : interpolateDefault(defaultValue, values);
};

const isSnippetDisabled = (snippet: CodeSnippet, commandManagementEnabled: boolean): boolean => {
  const availability = snippet.commandManagementAvailability ?? (snippet.requiresCommandManagement === true ? 'enabled' : undefined);

  if (availability === 'enabled') {
    return !commandManagementEnabled;
  }

  if (availability === 'disabled') {
    return commandManagementEnabled;
  }

  return false;
};

const searchableSnippetText = (snippet: CodeSnippet, t: TranslationFunction): string =>
  [
    snippet.key,
    snippet.title,
    getSnippetTitle(snippet, t),
    getSnippetDescription(snippet, t),
    snippet.more_info,
    snippet.code,
    snippet.display,
    ...(snippet.codeLines ?? []),
    ...(snippet.displayLines ?? []),
    ...(snippet.imports ?? []).flatMap((snippetImport) => [snippetImport.module, ...snippetImport.names]),
  ]
    .filter((value): value is string => Boolean(value))
    .join(' ')
    .toLowerCase();

const sourceMatches = (source: CodeSnippetSource | undefined, sourceFilter: CodeSnippetSourceFilter | undefined): boolean =>
  sourceFilter == null || sourceFilter === 'all' || source === sourceFilter;

export const snippetCardDomId = (metadataPath: string): string => {
  const slug = metadataPath
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
  return `snippet-card-${slug}`;
};

const filterSnippetTree = (
  snippet: CodeSnippet,
  terms: string[],
  t: TranslationFunction,
  placementFilter: CodeSnippetPlacementFilter | undefined,
  sourceFilter: CodeSnippetSourceFilter | undefined,
  commandManagementEnabled: boolean,
  hideDisabledSnippets: boolean,
  metadataPrefix: string,
  inheritedDropZone?: CodeSnippetDropZoneMode,
  inheritedSource?: CodeSnippetSource,
  ancestorTextMatched = false,
  cascadeTextMatches = true
): CodeSnippet | null => {
  const metadataPath = `${metadataPrefix}-${snippet.key}`;
  const resolvedDropZone = snippet.dropZone ?? inheritedDropZone;
  const resolvedSource = snippet.source ?? inheritedSource;
  const isDisabled = isSnippetDisabled(snippet, commandManagementEnabled);
  const hasChildren = (snippet.children?.length ?? 0) > 0;
  const ownTextMatched = terms.length === 0 || terms.every((term) => searchableSnippetText(snippet, t).includes(term));
  const textMatched = ancestorTextMatched || ownTextMatched;
  const childTextMatched = cascadeTextMatches && (ancestorTextMatched || ownTextMatched);

  const filteredChildren = snippet.children
    ?.map((child) =>
      filterSnippetTree(
        child,
        terms,
        t,
        placementFilter,
        sourceFilter,
        commandManagementEnabled,
        hideDisabledSnippets,
        metadataPath,
        resolvedDropZone,
        resolvedSource,
        childTextMatched,
        cascadeTextMatches
      )
    )
    .filter((child): child is CodeSnippet => child !== null);
  if (filteredChildren && filteredChildren.length > 0) {
    return {
      ...snippet,
      metadataPath,
      children: filteredChildren,
    };
  }

  if (hasChildren) {
    return null;
  }

  if (hideDisabledSnippets && isDisabled) {
    return null;
  }

  if (textMatched && codeTypeMatchesDropZone(resolvedDropZone, placementFilter) && sourceMatches(resolvedSource, sourceFilter)) {
    return { ...snippet, metadataPath };
  }

  return null;
};

interface AggregateExecutionModelSection {
  section: ExecutionModelSection;
  items: CodeSnippet[];
  resultCount: number;
}

const selectAggregateExecutionModel = (
  snippet: CodeSnippet,
  executionModel: CodeSnippetDropZone,
  placementFilter: CodeSnippetPlacementFilter | undefined,
  inheritedDropZone?: CodeSnippetDropZoneMode
): CodeSnippet | null => {
  const resolvedDropZone = snippet.dropZone ?? inheritedDropZone;

  if (snippet.children?.length) {
    const children = snippet.children
      .map((child) => selectAggregateExecutionModel(child, executionModel, placementFilter, resolvedDropZone))
      .filter((child): child is CodeSnippet => child !== null);

    return children.length > 0 ? { ...snippet, children } : null;
  }

  return aggregateExecutionModelForDropZone(resolvedDropZone, placementFilter) === executionModel ? snippet : null;
};

const countRenderedSnippetCards = (snippet: CodeSnippet): number =>
  snippet.children?.length ? snippet.children.reduce((total, child) => total + countRenderedSnippetCards(child), 0) : snippet.codeLines?.length ?? 1;

const codeForPlacement = (
  item: CodeSnippet,
  placementFilter: CodeSnippetPlacementFilter | undefined,
  field: 'codeByDropZone' | 'displayByDropZone'
): string | undefined => {
  const values = item[field];
  if (!values) return undefined;

  if (placementFilter === 'top-level-setup' || placementFilter === 'action-group') {
    return values[placementFilter];
  }

  return values['action-group'] ?? values['top-level-setup'];
};

// Reusable, theme-aware code renderers for Markdown
const InlineCode = ({ children }: { children: React.ReactNode }) => (
  <Box
    component="code"
    sx={(theme: Theme) => ({
      backgroundColor: 'action.hover',
      px: 0.5,
      borderRadius: 0.5,
      fontSize: (theme.typography as { code?: { fontSize?: string } }).code?.fontSize,
      fontFamily: (theme.typography as { code?: { fontFamily?: string } }).code?.fontFamily,
      lineHeight: 'inherit',
      display: 'inline-block',
    })}
  >
    {children}
  </Box>
);

const BlockCode = ({ children }: { children: React.ReactNode }) => (
  <Box
    component="pre"
    sx={{
      backgroundColor: 'action.hover',
      p: 1,
      borderRadius: 1,
      whiteSpace: 'pre-wrap',
      overflow: 'auto',
    }}
  >
    <Box
      component="code"
      sx={(theme: Theme) => ({
        fontSize: (theme.typography as { code?: { fontSize?: string } }).code?.fontSize,
        fontFamily: (theme.typography as { code?: { fontFamily?: string } }).code?.fontFamily,
      })}
    >
      {children}
    </Box>
  </Box>
);

const CodeContent = (props: CodeContentProps) => {
  const { t } = useTranslation();
  const navigationRootRef = useRef<HTMLDivElement | null>(null);
  const activeNavigationItemIdRef = useRef<string | null>(null);
  const [expandedMap, setExpandedMap] = useState<Record<string, boolean>>({});
  const [expandedExecutionModels, setExpandedExecutionModels] = useState<Record<CodeSnippetDropZoneMode, boolean>>(() => ({
    ...ALL_EXECUTION_MODELS_EXPANDED,
  }));
  const [autoExpandedExecutionModels, setAutoExpandedExecutionModels] = useState<Partial<Record<CodeSnippetDropZoneMode, boolean>>>({});
  const [disabledTooltipKey, setDisabledTooltipKey] = useState<string | null>(null);
  const categoryNavigation = props.categoryNavigation;
  const selectedCategoryKey = categoryNavigation?.selectedCategoryKey ?? null;
  const onSelectedCategoryHeaderChange = categoryNavigation?.onSelectedCategoryHeaderChange;
  const commandManagementEnabled = props.commandManagementEnabled;
  const hideDisabledSnippets = props.hideDisabledSnippets ?? false;
  const keyboardNavigation = props.keyboardNavigation;
  const keyboardNavigationEnabled = Boolean(keyboardNavigation);
  const onVisibleSnippetCountChange = keyboardNavigation?.onVisibleSnippetCountChange;
  const activationInstructions = t(
    'snippets.card.placementActivationInstructions',
    'Activate to choose an insertion location in the editor, or drag to place immediately.'
  );
  const keyboardNavigationInstructions = t(
    'snippets.navigation.instructions',
    'Use Up and Down Arrow to browse controls, Home and End to jump, Enter or Space to choose a snippet, Slash to focus the filter, and F6 or Shift+F6 to switch between snippets and the editor.'
  );
  const keyboardNavigationLabel = t('snippets.navigation.ariaLabel', 'API snippet commands');
  const executionModelLabels = props.groupSnippetsByExecutionModel
    ? {
        'top-level-setup': t('editorDrawer.apiFilter.codeType.regularCode', 'Immediate code'),
        'action-group': t('editorDrawer.apiFilter.codeType.robotBaseActions', 'RobotBase actions'),
      }
    : undefined;
  const executionModelSections: ExecutionModelSection[] = executionModelLabels
    ? [
        {
          dropZone: 'top-level-setup',
          title: executionModelLabels['top-level-setup'],
          description: t('snippets.api.executionModel.immediate.description', 'Runs when the program reaches it.'),
        },
        {
          dropZone: 'contextual',
          title: t('snippets.api.executionModel.contextual.title', 'Immediate code + RobotBase actions'),
          description: t(
            'editorDrawer.apiFilter.codeType.help',
            'Immediate code runs when the program reaches it. RobotBase actions run later when RobotBase starts them in routines or mappings.'
          ),
        },
        {
          dropZone: 'action-group',
          title: executionModelLabels['action-group'],
          description: t('snippets.api.executionModel.robotBaseActions.description', 'Run later when RobotBase starts them in routines or mappings.'),
        },
      ]
    : [];
  const filterTerms = useMemo(() => (props.filterText ?? '').trim().toLowerCase().split(/\s+/).filter(Boolean), [props.filterText]);
  const isSearchActive = filterTerms.length > 0;
  const shouldAutoRevealExecutionModels =
    props.groupSnippetsByExecutionModel && (isSearchActive || (props.placementFilter ?? 'all') !== 'all' || (props.sourceFilter ?? 'all') !== 'all');

  useEffect(() => {
    setAutoExpandedExecutionModels(shouldAutoRevealExecutionModels ? { ...ALL_EXECUTION_MODELS_EXPANDED } : {});
  }, [props.filterText, props.placementFilter, props.sourceFilter, shouldAutoRevealExecutionModels]);

  const handleExecutionModelExpansionChange = (dropZone: CodeSnippetDropZoneMode, expanded: boolean) => {
    setExpandedExecutionModels((current) => ({ ...current, [dropZone]: expanded }));
    setAutoExpandedExecutionModels((current) => {
      if (current[dropZone] === undefined) return current;

      const next = { ...current };
      delete next[dropZone];
      return next;
    });
  };
  const visibleChildren = useMemo(
    () =>
      props.snippet.children
        ?.map((item) =>
          filterSnippetTree(
            item,
            filterTerms,
            t,
            props.placementFilter,
            props.sourceFilter,
            commandManagementEnabled,
            hideDisabledSnippets,
            props.snippet.key,
            props.snippet.dropZone,
            props.snippet.source,
            false,
            !isSearchActive
          )
        )
        .filter((item): item is CodeSnippet => item !== null) ?? [],
    [
      props.snippet.children,
      props.snippet.dropZone,
      props.snippet.key,
      props.snippet.source,
      props.placementFilter,
      props.sourceFilter,
      commandManagementEnabled,
      hideDisabledSnippets,
      filterTerms,
      isSearchActive,
      t,
    ]
  );
  const selectedCategory = useMemo(
    () => (!isSearchActive && selectedCategoryKey ? visibleChildren.find((item) => item.key === selectedCategoryKey) ?? null : null),
    [isSearchActive, selectedCategoryKey, visibleChildren]
  );
  const selectedCategoryHeader = useMemo<CodeSnippetCategoryHeader | null>(
    () =>
      selectedCategory
        ? {
            title: getSnippetTitle(selectedCategory, t),
            description: getSnippetDescription(selectedCategory, t),
            ...(selectedCategory.categorySection ? { sectionTitle: getCategorySectionTitle(selectedCategory.categorySection, t) } : {}),
          }
        : null,
    [selectedCategory, t]
  );
  const visibleSnippetCount = useMemo(
    () =>
      selectedCategory
        ? countRenderedSnippetCards(selectedCategory)
        : visibleChildren.reduce((total, item) => total + countRenderedSnippetCards(item), 0),
    [selectedCategory, visibleChildren]
  );

  useEffect(() => {
    onVisibleSnippetCountChange?.(visibleSnippetCount);
  }, [onVisibleSnippetCountChange, visibleSnippetCount]);

  const getNavigationItems = useCallback((): HTMLElement[] => {
    if (!keyboardNavigationEnabled) return [];

    return Array.from(navigationRootRef.current?.querySelectorAll<HTMLElement>(API_SNIPPET_NAVIGATION_ITEM_SELECTOR) ?? []).filter(
      (item) => !item.hasAttribute('disabled') && item.getAttribute('aria-disabled') !== 'true'
    );
  }, [keyboardNavigationEnabled]);

  const setActiveNavigationItem = useCallback(
    (nextItem: HTMLElement | null) => {
      const navigationItems = getNavigationItems();
      for (const item of navigationItems) {
        item.tabIndex = item === nextItem ? 0 : -1;
      }
      activeNavigationItemIdRef.current = nextItem?.id ?? null;
    },
    [getNavigationItems]
  );

  useLayoutEffect(() => {
    if (!keyboardNavigationEnabled) return;

    const navigationItems = getNavigationItems();
    const rememberedItem = navigationItems.find((item) => item.id === activeNavigationItemIdRef.current);
    setActiveNavigationItem(rememberedItem ?? navigationItems[0] ?? null);
  });

  const handleNavigationTarget = (event: React.SyntheticEvent<HTMLDivElement>) => {
    const target = event.target instanceof Element ? event.target.closest<HTMLElement>(API_SNIPPET_NAVIGATION_ITEM_SELECTOR) : null;
    if (target && navigationRootRef.current?.contains(target) && getNavigationItems().includes(target)) {
      setActiveNavigationItem(target);
    }
  };

  const handleNavigationKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    const currentItem = event.target instanceof Element ? event.target.closest<HTMLElement>(API_SNIPPET_NAVIGATION_ITEM_SELECTOR) : null;
    if (!currentItem || !navigationRootRef.current?.contains(currentItem)) return;

    const navigationItems = getNavigationItems();
    const currentIndex = navigationItems.indexOf(currentItem);
    if (currentIndex < 0) return;

    let nextIndex: number;
    switch (event.key) {
      case 'ArrowDown':
        nextIndex = Math.min(currentIndex + 1, navigationItems.length - 1);
        break;
      case 'ArrowUp':
        nextIndex = Math.max(currentIndex - 1, 0);
        break;
      case 'Home':
        nextIndex = 0;
        break;
      case 'End':
        nextIndex = navigationItems.length - 1;
        break;
      default:
        return;
    }

    event.preventDefault();
    event.stopPropagation();
    const nextItem = navigationItems[nextIndex];
    setActiveNavigationItem(nextItem);
    nextItem.focus();
  };

  useEffect(() => {
    onSelectedCategoryHeaderChange?.(selectedCategoryHeader);
  }, [onSelectedCategoryHeaderChange, selectedCategoryHeader]);

  useEffect(() => {
    if (!disabledTooltipKey) return;

    const closeTooltip = () => setDisabledTooltipKey(null);

    window.addEventListener('scroll', closeTooltip, true);
    window.addEventListener('wheel', closeTooltip, true);
    window.addEventListener('touchmove', closeTooltip, true);

    return () => {
      window.removeEventListener('scroll', closeTooltip, true);
      window.removeEventListener('wheel', closeTooltip, true);
      window.removeEventListener('touchmove', closeTooltip, true);
    };
  }, [disabledTooltipKey]);

  const renderSnippetIcon = (item: CodeSnippet) =>
    item.iconComponent ? (
      <Box
        component={item.iconComponent}
        aria-hidden
        sx={{
          width: 20,
          height: 20,
          flexShrink: 0,
          transform: 'translate(2px, -1px)',
          color: 'primary.main',
        }}
      />
    ) : item.icon ? (
      <Box
        sx={{
          width: 20,
          height: 20,
          flexShrink: 0,
          transform: 'translate(2px, -1px)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: 'primary.main',
          '& > *': {
            width: '100%',
            height: '100%',
          },
        }}
      >
        {item.icon}
      </Box>
    ) : null;

  const renderEmptyState = () => (
    <Box textAlign="center" width="100%" p={2}>
      <Typography variant="body2" color="text.secondary">
        {props.emptyFilterMessage ?? t('snippets.filter.empty', 'No snippets match that filter.')}
      </Typography>
    </Box>
  );

  const renderCategoryItem = (item: CodeSnippet) => {
    const title = getSnippetTitle(item, t);
    const description = getSnippetDescription(item, t);

    return (
      <Paper key={`category-${item.key}`} sx={{ width: '100%', borderRadius: 1 }}>
        <ButtonBase
          id={`snippet-category-${item.key}`}
          data-testid={`snippet-category-${item.key}`}
          data-snippet-navigation-item={keyboardNavigationEnabled ? 'true' : undefined}
          tabIndex={keyboardNavigationEnabled ? -1 : undefined}
          onClick={() => categoryNavigation?.onSelectCategory(item.key)}
          aria-label={t('snippets.category.openAriaLabel', 'Open {{title}} snippets', { title })}
          sx={{
            width: '100%',
            display: 'block',
            textAlign: 'left',
            p: 1.5,
            borderRadius: 1,
            '& .snippet-category-chevron': {
              color: 'text.secondary',
              opacity: 0.55,
              transform: 'translateX(-2px)',
              transition: (theme) =>
                theme.transitions.create(['color', 'opacity', 'transform'], {
                  duration: theme.transitions.duration.shortest,
                }),
              '@media (hover: hover)': {
                opacity: 0.28,
              },
              '@media (hover: none)': {
                opacity: 0.7,
                transform: 'translateX(0)',
              },
            },
            '&:hover .snippet-category-chevron, &.Mui-focusVisible .snippet-category-chevron': {
              color: 'text.primary',
              opacity: 1,
              transform: 'translateX(0)',
            },
          }}
        >
          <Stack direction="row" alignItems="center" spacing={1}>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Stack direction="row" alignItems="center" spacing={0.75}>
                {renderSnippetIcon(item)}
                <Typography variant="h6" component="div" sx={{ userSelect: 'none', lineHeight: 1.2, minWidth: 0 }}>
                  {title}
                </Typography>
              </Stack>
              {description ? (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  {description}
                </Typography>
              ) : null}
            </Box>
            <Box sx={{ alignItems: 'center', display: 'flex', flexShrink: 0, justifyContent: 'center', width: 20 }}>
              <ChevronRightIcon className="snippet-category-chevron" fontSize="small" />
            </Box>
          </Stack>
        </ButtonBase>
      </Paper>
    );
  };

  const renderCategoryBackControl = () => (
    <Button
      id={`snippet-category-${props.snippet.key}-back`}
      data-snippet-navigation-item={keyboardNavigationEnabled ? 'true' : undefined}
      tabIndex={keyboardNavigationEnabled ? -1 : undefined}
      startIcon={<ArrowBackIcon fontSize="small" />}
      onClick={props.categoryNavigation?.onBack}
      sx={{ alignSelf: 'flex-start' }}
    >
      {t('snippets.category.back', 'Back to categories')}
    </Button>
  );

  const renderExecutionModelSection = (section: ExecutionModelSection, content: React.ReactNode, sectionKey: string, resultCount?: number) => {
    const headingId = `${sectionKey}-heading`;
    const contentId = `${sectionKey}-content`;
    const sectionExpanded = autoExpandedExecutionModels[section.dropZone] ?? expandedExecutionModels[section.dropZone];

    return (
      <Accordion
        key={sectionKey}
        data-testid={`snippet-execution-section-${section.dropZone}`}
        disableGutters
        elevation={0}
        expanded={sectionExpanded}
        onChange={(_event, expanded) => handleExecutionModelExpansionChange(section.dropZone, expanded)}
        slotProps={{ transition: { unmountOnExit: true } }}
        sx={{
          backgroundColor: 'transparent',
          border: 1,
          borderColor: 'divider',
          borderRadius: 1,
          overflow: 'hidden',
          width: '100%',
          '&::before': { display: 'none' },
        }}
      >
        <AccordionSummary
          id={headingId}
          data-snippet-navigation-item={keyboardNavigationEnabled ? 'true' : undefined}
          tabIndex={keyboardNavigationEnabled ? -1 : undefined}
          aria-controls={contentId}
          expandIcon={<ExpandMoreIcon fontSize="small" />}
          sx={{
            backgroundColor: 'action.selected',
            minHeight: 0,
            px: 1,
            '&.Mui-expanded': { minHeight: 0 },
            '&:hover': { backgroundColor: 'action.focus' },
            '& .MuiAccordionSummary-content': { my: 0.75 },
            '& .MuiAccordionSummary-content.Mui-expanded': { my: 0.75 },
            '& .MuiAccordionSummary-expandIconWrapper': { color: 'text.secondary', ml: 0.75 },
          }}
        >
          <Box sx={{ minWidth: 0, flexGrow: 1 }}>
            <Stack direction="row" alignItems="center" spacing={0.75}>
              <Typography component="span" variant="subtitle2" sx={{ display: 'block', fontWeight: 700, lineHeight: 1.25 }}>
                {section.title}
              </Typography>
              {resultCount === undefined ? null : (
                <Typography
                  component="span"
                  variant="caption"
                  color="text.secondary"
                  aria-hidden
                  data-testid={`snippet-execution-section-count-${section.dropZone}`}
                  sx={{
                    backgroundColor: 'action.hover',
                    borderRadius: 2,
                    minWidth: 20,
                    px: 0.75,
                    textAlign: 'center',
                    fontWeight: 700,
                    lineHeight: 1.5,
                  }}
                >
                  {resultCount}
                </Typography>
              )}
            </Stack>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1.3, mt: 0.25 }}>
              {section.description}
            </Typography>
          </Box>
        </AccordionSummary>
        <AccordionDetails sx={{ p: 1 }}>{content}</AccordionDetails>
      </Accordion>
    );
  };

  const renderSnippetItem = (
    item: CodeSnippet,
    parentType: string | undefined,
    parentDropZone: CodeSnippetDropZoneMode | undefined,
    parentImports: CodeSnippetImport[] | undefined,
    keyPrefix: string,
    options: { groupChildrenByExecutionModel?: boolean; hideOwnHeader?: boolean } = {}
  ) => {
    const itemType = item.type ?? parentType;
    const itemDropZone = item.dropZone ?? parentDropZone;
    const itemImports = item.imports ?? parentImports;
    const contextualExecutionModelLabels = itemDropZone === 'contextual' ? executionModelLabels : undefined;
    const frameworkClassName = item.frameworkClassName;
    const hasChildren = item.children && item.children.length > 0;
    const isDisabled = isSnippetDisabled(item, commandManagementEnabled);
    const disabledReason = isDisabled ? getSnippetDisabledReason(item, t) : undefined;
    const title = getSnippetTitle(item, t);
    const description = getSnippetDescription(item, t);
    const itemKey = `${keyPrefix}-${item.key}`;
    const isDisabledLeaf = isDisabled && !hasChildren;
    const isLeaf = !hasChildren;
    const showGroupHeader = hasChildren && !options.hideOwnHeader;
    const showLeafHeader = isLeaf;
    const renderChildItems = (children: CodeSnippet[]) => (
      <Stack direction="column" spacing={1.25} sx={{ width: '100%' }}>
        {children.map((child) => renderSnippetItem(child, itemType, itemDropZone, itemImports, `${keyPrefix}-${item.key}`))}
      </Stack>
    );
    const shouldGroupChildren = options.groupChildrenByExecutionModel && executionModelSections.length > 0;
    const ungroupedChildren = shouldGroupChildren ? item.children?.filter((child) => child.dropZone == null && itemDropZone == null) ?? [] : [];
    const childSnippets = hasChildren ? (
      shouldGroupChildren ? (
        <Stack key={itemKey} direction="column" spacing={2} sx={{ width: '100%' }}>
          {executionModelSections.map((section) => {
            const sectionChildren = item.children?.filter((child) => (child.dropZone ?? itemDropZone) === section.dropZone) ?? [];
            if (sectionChildren.length === 0) return null;

            return renderExecutionModelSection(section, renderChildItems(sectionChildren), `${itemKey}-${section.dropZone}`);
          })}
          {ungroupedChildren.length > 0 ? renderChildItems(ungroupedChildren) : null}
        </Stack>
      ) : (
        renderChildItems(item.children ?? [])
      )
    ) : null;

    if (hasChildren && item.presentation === 'section') {
      return (
        <Stack key={itemKey} direction="column" spacing={1} sx={{ width: '100%' }} data-testid={`snippet-section-${item.key}`}>
          <Typography variant="subtitle2" component="h4" color="text.secondary" sx={{ px: 0.5, fontWeight: 700 }}>
            {title}
          </Typography>
          {childSnippets}
        </Stack>
      );
    }

    if (hasChildren && options.hideOwnHeader) {
      return childSnippets;
    }

    const snippetItem = (
      <Paper
        key={itemKey}
        aria-disabled={isDisabledLeaf ? true : undefined}
        sx={{
          height: 'auto',
          padding: isLeaf ? 1.25 : 1.5,
          width: '100%',
          border: isLeaf ? 1 : undefined,
          borderColor: isDisabledLeaf ? 'action.disabledBackground' : 'divider',
          borderRadius: 1.5,
          boxShadow: isLeaf ? 'none' : undefined,
          backgroundColor: isLeaf ? (isDisabledLeaf ? 'action.disabledBackground' : 'action.hover') : undefined,
          display: 'flex',
          flexDirection: 'column',
          flexGrow: 1,
          cursor: isDisabledLeaf ? 'not-allowed' : undefined,
          transition: (theme) =>
            theme.transitions.create(['background-color', 'border-color'], {
              duration: theme.transitions.duration.shortest,
            }),
        }}
      >
        {showGroupHeader ? (
          <Stack direction="row" alignItems="center" spacing={0.75} sx={{ marginBottom: 1 }}>
            {renderSnippetIcon(item)}
            <Typography variant="h6" component="div" sx={{ userSelect: 'none', lineHeight: 1, flexGrow: 1 }}>
              {title}
            </Typography>
          </Stack>
        ) : null}
        {showLeafHeader ? (
          <Stack direction="row" alignItems="center" spacing={0.75} sx={{ marginBottom: 1 }}>
            {renderSnippetIcon(item)}
            <Typography variant="subtitle2" component="div" sx={{ userSelect: 'none', lineHeight: 1.2, flexGrow: 1 }}>
              {title}
            </Typography>
          </Stack>
        ) : null}
        {showGroupHeader && description && (
          <Typography variant="body2" sx={{ marginBottom: 1 }}>
            {description}
          </Typography>
        )}
        {hasChildren ? (
          childSnippets
        ) : item.codeLines && item.codeLines.length > 0 ? (
          <Stack direction="column" spacing={1.25}>
            {item.codeLines.map((line, idx) => {
              const isGroup = item.type === 'group';
              const code = isGroup ? `${line}\n)` : `${line},`;
              const display = item.displayLines ? item.displayLines[idx] : undefined;
              const lineTitle =
                item.codeLines!.length === 1
                  ? title
                  : t('snippets.card.lineTitle', '{{title}}, snippet {{index}} of {{count}}', {
                      title,
                      index: idx + 1,
                      count: item.codeLines!.length,
                    });
              return (
                <CodeEmbed
                  key={`${item.key}-line-${idx}`}
                  id={snippetCardDomId(`${item.metadataPath ?? itemKey}-line-${idx}`)}
                  title={lineTitle}
                  testId={`snippet-${item.key}-line-${idx}`}
                  draggableId={line}
                  code={code}
                  type={itemType}
                  dropZone={itemDropZone}
                  imports={itemImports}
                  frameworkClassName={frameworkClassName}
                  display={display}
                  receiverClassName={item.receiverClassName}
                  supportingReceiver={item.supportingReceiver}
                  disabled={isDisabled}
                  disabledReason={disabledReason}
                  activationInstructions={activationInstructions}
                  onActivate={props.onInsertSnippet}
                  executionModelLabels={contextualExecutionModelLabels}
                  manageKeyboardNavigation={keyboardNavigationEnabled}
                />
              );
            })}
          </Stack>
        ) : (
          <CodeEmbed
            id={snippetCardDomId(item.metadataPath ?? itemKey)}
            title={title}
            testId={`snippet-${item.key}`}
            code={item.code ?? item.codeByDropZone?.['top-level-setup'] ?? item.codeByDropZone?.['action-group'] ?? ''}
            insertText={item.insertText}
            type={itemType}
            dropZone={itemDropZone}
            codeByDropZone={item.codeByDropZone}
            imports={itemImports}
            frameworkClassName={frameworkClassName}
            display={codeForPlacement(item, props.placementFilter, 'displayByDropZone') ?? item.display}
            receiverClassName={item.receiverClassName}
            supportingReceiver={item.supportingReceiver}
            disabled={isDisabled}
            disabledReason={disabledReason}
            activationInstructions={activationInstructions}
            onActivate={props.onInsertSnippet}
            executionModelLabels={contextualExecutionModelLabels}
            manageKeyboardNavigation={keyboardNavigationEnabled}
          />
        )}
        {!hasChildren && description && (
          <Typography variant="body2" sx={{ marginTop: 1 }}>
            {description}
          </Typography>
        )}
        {item.more_info && (
          <Box sx={{ marginTop: 0.5, width: '100%' }}>
            <Link
              id={`${snippetCardDomId(item.metadataPath ?? itemKey)}-more-info`}
              data-snippet-navigation-item={keyboardNavigationEnabled ? 'true' : undefined}
              tabIndex={keyboardNavigationEnabled ? -1 : undefined}
              component="button"
              type="button"
              onClick={() =>
                setExpandedMap((prev) => ({
                  ...prev,
                  [item.key]: !prev[item.key],
                }))
              }
              underline="hover"
              sx={{ padding: 0, alignSelf: 'flex-start', fontSize: 'body2.fontSize' }}
            >
              {expandedMap[item.key] ? t('ui.show-less', 'Show less') : t('ui.show-more', 'Show more')}
            </Link>
            <Collapse in={!!expandedMap[item.key]} timeout="auto" unmountOnExit>
              <Box
                sx={{
                  marginTop: 1,
                  '& a': { color: 'primary.main' },
                  '& img': {
                    maxWidth: '100%',
                    height: 'auto',
                    display: 'block',
                    borderRadius: 1,
                  },
                }}
              >
                <ReactMarkdown
                  remarkPlugins={[remarkGfm]}
                  rehypePlugins={[rehypeSanitize]}
                  components={{
                    a: (props: { href?: string; children?: React.ReactNode }) => {
                      const { href, children } = props;
                      return (
                        <Link
                          id={`${snippetCardDomId(`${item.metadataPath ?? itemKey}-more-info-${href ?? 'link'}`)}-link`}
                          data-snippet-navigation-item={keyboardNavigationEnabled ? 'true' : undefined}
                          tabIndex={keyboardNavigationEnabled ? -1 : undefined}
                          href={href ?? '#'}
                          target="_blank"
                          rel="noopener noreferrer"
                          sx={{ fontSize: 'body2.fontSize' }}
                        >
                          {children}
                        </Link>
                      );
                    },
                    p: (props: { children?: React.ReactNode }) => {
                      const { children } = props;
                      return <Typography variant="body2">{children}</Typography>;
                    },
                    img: (props: { src?: string; alt?: string }) => {
                      const { src, alt } = props;
                      return (
                        <Box
                          component="img"
                          src={src}
                          alt={alt ?? ''}
                          draggable={false}
                          sx={{ maxWidth: '100%', height: 'auto', display: 'block', borderRadius: 1 }}
                        />
                      );
                    },
                    code: (props: { inline?: boolean; children?: React.ReactNode }) => {
                      const { inline, children } = props;
                      return inline ? <InlineCode>{children}</InlineCode> : <BlockCode>{children}</BlockCode>;
                    },
                  }}
                >
                  {normalizeMarkdown(item.more_info ?? '')}
                </ReactMarkdown>
              </Box>
            </Collapse>
          </Box>
        )}
      </Paper>
    );

    if (!isDisabledLeaf || !disabledReason) {
      return snippetItem;
    }

    return (
      <Tooltip
        key={itemKey}
        title={disabledReason}
        arrow
        placement="top"
        enterDelay={150}
        enterNextDelay={0}
        open={disabledTooltipKey === itemKey}
        onOpen={() => setDisabledTooltipKey(itemKey)}
        onClose={() => setDisabledTooltipKey(null)}
      >
        {snippetItem}
      </Tooltip>
    );
  };

  const asExecutionModelGroup = (item: CodeSnippet): CodeSnippet =>
    props.groupSnippetsByExecutionModel && !item.children?.length
      ? {
          key: `${item.key}-execution-model-group`,
          title: item.title,
          titleI18n: item.titleI18n,
          children: [item],
        }
      : item;

  const aggregateExecutionModelSections = EXECUTION_MODEL_DROP_ZONES.map((dropZone) => {
    const section = executionModelSections.find((candidate) => candidate.dropZone === dropZone);
    if (!section) return null;

    const items = visibleChildren
      .map((item) => selectAggregateExecutionModel(item, dropZone, props.placementFilter, props.snippet.dropZone))
      .filter((item): item is CodeSnippet => item !== null);
    const resultCount = items.reduce((total, item) => total + countRenderedSnippetCards(item), 0);

    return resultCount > 0 ? { section, items, resultCount } : null;
  }).filter((result): result is AggregateExecutionModelSection => result !== null);
  const visibleSearchResultCount = visibleChildren.reduce((total, item) => total + countRenderedSnippetCards(item), 0);
  const aggregatedSearchResultCount = aggregateExecutionModelSections.reduce((total, result) => total + result.resultCount, 0);
  const canAggregateSearchResults =
    props.groupSnippetsByExecutionModel && aggregateExecutionModelSections.length > 0 && aggregatedSearchResultCount === visibleSearchResultCount;

  const renderSearchResultCategory = (
    item: CodeSnippet,
    parentType: string | undefined,
    parentDropZone: CodeSnippetDropZoneMode | undefined,
    parentImports: CodeSnippetImport[] | undefined,
    keyPrefix: string,
    options: { groupChildrenByExecutionModel?: boolean; showCategoryTestId?: boolean } = {}
  ) => {
    const hasChildren = (item.children?.length ?? 0) > 0;

    if (!hasChildren) {
      if (!options.groupChildrenByExecutionModel) {
        return renderSnippetItem(item, parentType, parentDropZone, parentImports, keyPrefix);
      }

      return renderSnippetItem(asExecutionModelGroup(item), parentType, parentDropZone, parentImports, keyPrefix, {
        groupChildrenByExecutionModel: true,
        hideOwnHeader: true,
      });
    }

    return (
      <Stack key={`${keyPrefix}-${item.key}`} direction="column" spacing={0.75} sx={{ width: '100%' }}>
        <Typography
          variant="caption"
          color="text.secondary"
          data-testid={options.showCategoryTestId ? `snippet-search-category-${item.key}` : undefined}
          sx={{ px: 0.5, fontWeight: 700 }}
        >
          {getSnippetTitle(item, t)}
        </Typography>
        {renderSnippetItem(item, parentType, parentDropZone, parentImports, keyPrefix, {
          groupChildrenByExecutionModel: options.groupChildrenByExecutionModel,
          hideOwnHeader: true,
        })}
      </Stack>
    );
  };

  const renderAggregateSearchResults = () => (
    <Stack direction="column" spacing={2} sx={{ width: '100%' }}>
      {aggregateExecutionModelSections.map(({ section, items, resultCount }) =>
        renderExecutionModelSection(
          section,
          <Stack direction="column" spacing={1.25} sx={{ width: '100%' }}>
            {items.map((item) =>
              renderSearchResultCategory(
                item,
                props.snippet.type,
                props.snippet.dropZone,
                props.snippet.imports,
                `search-${props.snippet.key}-${section.dropZone}`,
                { showCategoryTestId: true }
              )
            )}
          </Stack>,
          `search-${props.snippet.key}-${section.dropZone}`,
          resultCount
        )
      )}
    </Stack>
  );

  const renderCategoryList = () =>
    visibleChildren.map((item, index) => {
      const section = item.categorySection;
      const previousSectionKey = visibleChildren[index - 1]?.categorySection?.key;
      const showSectionHeading = section && section.key !== previousSectionKey;

      return (
        <Stack key={`category-list-${item.key}`} direction="column" spacing={showSectionHeading ? 0.75 : 0} sx={{ width: '100%' }}>
          {showSectionHeading ? (
            <Typography
              variant="h6"
              component="h3"
              data-testid={`snippet-category-section-${section.key}`}
              sx={{ px: 0.5, pt: index === 0 ? 0 : 0.5 }}
            >
              {getCategorySectionTitle(section, t)}
            </Typography>
          ) : null}
          {renderCategoryItem(item)}
        </Stack>
      );
    });

  return (
    <Stack
      ref={navigationRootRef}
      id={keyboardNavigation?.containerId}
      data-api-snippet-pane={keyboardNavigationEnabled ? 'true' : undefined}
      role={keyboardNavigationEnabled ? 'toolbar' : undefined}
      aria-orientation={keyboardNavigationEnabled ? 'vertical' : undefined}
      aria-label={keyboardNavigationEnabled ? keyboardNavigationLabel : undefined}
      aria-describedby={keyboardNavigation?.instructionsId}
      onFocusCapture={keyboardNavigationEnabled ? handleNavigationTarget : undefined}
      onPointerDownCapture={keyboardNavigationEnabled ? handleNavigationTarget : undefined}
      onKeyDownCapture={keyboardNavigationEnabled ? handleNavigationKeyDown : undefined}
      direction="column"
      width={'100%'}
      spacing={1}
      sx={{ padding: 1 }}
    >
      {keyboardNavigation ? (
        <Box
          id={keyboardNavigation.instructionsId}
          component="span"
          sx={{
            border: 0,
            clip: 'rect(0 0 0 0)',
            height: 1,
            margin: -1,
            overflow: 'hidden',
            padding: 0,
            position: 'absolute',
            whiteSpace: 'nowrap',
            width: 1,
          }}
        >
          {keyboardNavigationInstructions}
        </Box>
      ) : null}
      <Stack direction="column" justifyContent="flex-start" alignItems="flex-start" spacing={1} sx={{ width: '100%' }}>
        {props.extraContentTop ? (
          <Box width={'100%'}>{props.extraContentTop}</Box>
        ) : (
          <Box textAlign={'center'} width={'100%'} p={2}>
            <Typography textAlign={'center'}>
              <Trans i18nKey={`${props.snippet.key}.tab.header`}>Text placeholder for the {props.snippet.title} tab</Trans>
            </Typography>
          </Box>
        )}
        {categoryNavigation ? (
          isSearchActive ? (
            canAggregateSearchResults ? (
              renderAggregateSearchResults()
            ) : (
              visibleChildren.map((item) =>
                renderSearchResultCategory(item, props.snippet.type, props.snippet.dropZone, props.snippet.imports, `search-${props.snippet.key}`, {
                  groupChildrenByExecutionModel: props.groupSnippetsByExecutionModel,
                })
              )
            )
          ) : selectedCategoryKey ? (
            <>
              {categoryNavigation.hideBackControl ? null : renderCategoryBackControl()}
              {selectedCategory
                ? renderSnippetItem(
                    asExecutionModelGroup(selectedCategory),
                    props.snippet.type,
                    props.snippet.dropZone,
                    props.snippet.imports,
                    `item-${props.snippet.key}`,
                    {
                      groupChildrenByExecutionModel: props.groupSnippetsByExecutionModel,
                      hideOwnHeader: categoryNavigation.hideSelectedCategoryHeader,
                    }
                  )
                : renderEmptyState()}
            </>
          ) : (
            renderCategoryList()
          )
        ) : (
          visibleChildren.map((item) => renderSnippetItem(item, props.snippet.type, props.snippet.dropZone, props.snippet.imports, props.snippet.key))
        )}
        {props.snippet.children &&
          props.snippet.children.length > 0 &&
          visibleChildren.length === 0 &&
          (!selectedCategoryKey || isSearchActive) &&
          renderEmptyState()}
      </Stack>
    </Stack>
  );
};

export default CodeContent;
